// Lifecycle mutations for an OT — create / patch / archive / advance / jump.
// The detail body comes back on every successful response, so we slot it
// straight into the cache and let `invalidateQueries` cover the list.

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  advanceOT,
  archiveOT,
  createOT,
  jumpOT,
  patchOT,
  unarchiveOT,
} from "@/features/transit/api/transit.api"
import type {
  CreateOTPayload,
  JumpStagePayload,
  OT,
  PatchOTPayload,
} from "@/features/transit/api/types"
import { transitKeys } from "@/features/transit/hooks/use-transit-orders"
import { resolveApiError } from "@/lib/api/error-i18n"

// Invalidates the OT detail + list queries and **refetches** the active
// detail synchronously so the next render sees the post-mutation server
// state. Returning a Promise from `onSuccess` makes React Query wait
// for the refetch before calling `mutate`'s consumer-level callbacks.
function useTransitInvalidator() {
  const queryClient = useQueryClient()
  return async (otId?: string | number) => {
    if (otId !== undefined) {
      await queryClient.refetchQueries({
        queryKey: transitKeys.detail(otId),
        type: "active",
      })
    }
    await queryClient.invalidateQueries({ queryKey: transitKeys.all })
  }
}

// Some mutation responses return the refreshed OT detail. We *seed* the
// cache with that response so the UI doesn't flash an empty state while
// the refetch resolves. The refetch above remains the source of truth.
export function useSetTransitDetail() {
  const queryClient = useQueryClient()
  return (ot: OT) => {
    if (!ot || typeof ot !== "object" || !("id" in ot)) return
    queryClient.setQueryData(transitKeys.detail(ot.id), ot)
  }
}

export function useCreateOT() {
  const { t } = useTranslation("transit")
  const invalidate = useTransitInvalidator()
  const setDetail = useSetTransitDetail()
  return useMutation({
    mutationFn: (payload: CreateOTPayload) => createOT(payload),
    onSuccess: async (ot) => {
      toast.success(t("toast.created", { otNumber: ot.otNumber }))
      setDetail(ot)
      await invalidate(ot.id)
    },
    onError: (error) => {
      toast.error(resolveApiError(error, t))
    },
  })
}

export function usePatchOT(otId: number | string) {
  const { t } = useTranslation("transit")
  const invalidate = useTransitInvalidator()
  const setDetail = useSetTransitDetail()
  return useMutation({
    mutationFn: (payload: PatchOTPayload) => patchOT(otId, payload),
    onSuccess: async (ot) => {
      setDetail(ot)
      await invalidate(otId)
    },
    onError: (error) => {
      toast.error(resolveApiError(error, t))
    },
  })
}

export function useArchiveOT(otId: number | string) {
  const { t } = useTranslation("transit")
  const invalidate = useTransitInvalidator()
  const setDetail = useSetTransitDetail()
  return useMutation({
    mutationFn: () => archiveOT(otId),
    onSuccess: async (ot) => {
      toast.success(t("toast.archived"))
      setDetail(ot)
      await invalidate(otId)
    },
    onError: (error) => {
      toast.error(resolveApiError(error, t))
    },
  })
}

export function useUnarchiveOT(otId: number | string) {
  const { t } = useTranslation("transit")
  const invalidate = useTransitInvalidator()
  const setDetail = useSetTransitDetail()
  return useMutation({
    mutationFn: () => unarchiveOT(otId),
    onSuccess: async (ot) => {
      toast.success(t("toast.unarchived"))
      setDetail(ot)
      await invalidate(otId)
    },
    onError: (error) => {
      toast.error(resolveApiError(error, t))
    },
  })
}

// `advance` is the one place where we don't toast on error. The 422
// response body is the OT detail with `blockers[]` populated; we drop
// it into the cache so the panel re-renders the new blocker list. The
// caller can still observe `error` to show a non-blocker network failure.
export function useAdvanceOT(otId: number | string) {
  const setDetail = useSetTransitDetail()
  const invalidate = useTransitInvalidator()
  const { t } = useTranslation("transit")
  return useMutation({
    mutationFn: () => advanceOT(otId),
    onSuccess: async (ot) => {
      toast.success(t("toast.stageAdvanced", { stage: ot.stage }))
      setDetail(ot)
      await invalidate(otId)
    },
    onError: (error) => {
      toast.error(resolveApiError(error, t))
    },
  })
}

export function useJumpOT(otId: number | string) {
  const { t } = useTranslation("transit")
  const setDetail = useSetTransitDetail()
  const invalidate = useTransitInvalidator()
  return useMutation({
    mutationFn: (payload: JumpStagePayload) => jumpOT(otId, payload),
    onSuccess: async (ot) => {
      setDetail(ot)
      await invalidate(otId)
    },
    onError: (error) => {
      toast.error(resolveApiError(error, t))
    },
  })
}
